import React, { useState, useEffect, useRef } from 'react';
import { api } from '../lib/api';
import { Plane, Search } from 'lucide-react';
import { Popover, PopoverContent, PopoverAnchor } from './ui/popover';

const fmt = (v) => (!v ? '' : (v.all || (v.code || '').includes(',')) ? `${v.city} (All airports)` : `${v.city} (${v.code})`);

// Airport typeahead. Controlled value is an airport object {code, city, ...}.
export const AirportAutocomplete = ({ label, placeholder, value, onChange, testid, icon: Icon = Plane }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [display, setDisplay] = useState(fmt(value));
  const [editing, setEditing] = useState(false);
  const ref = useRef(null);

  // Keep the field in sync with the selected value — but never clobber what the user is typing.
  useEffect(() => {
    if (!editing) setDisplay(fmt(value));
  }, [value, editing]);

  useEffect(() => {
    let active = true;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.airports(query);
        if (active) setResults(data.results || []);
      } catch (e) { /* noop */ }
    }, 150);
    return () => { active = false; clearTimeout(t); };
  }, [query]);

  const pick = (ap) => {
    onChange(ap);
    setEditing(false);
    setDisplay(fmt(ap));
    setQuery('');
    setOpen(false);
  };

  // Clicking/focusing the field to change it wipes the text so the user types fresh
  // (fixes the old "Dubai (DXB)Mumbai" name mix-up), and opens the list.
  const beginEdit = () => {
    if (!editing) { setEditing(true); setDisplay(''); setQuery(''); }
    setOpen(true);
  };

  const handleOpenChange = (next) => {
    setOpen(next);
    if (!next) { setEditing(false); setDisplay(fmt(value)); } // closed without picking → restore
  };

  // Group results by city so multi-airport cities (e.g. Goa → GOI + GOX) get a combined
  // "All airports" option that searches every airport of that city and merges the results.
  const groups = React.useMemo(() => {
    const byCity = {};
    for (const ap of results) {
      const key = `${ap.city}|${ap.country_code || ap.country}`;
      (byCity[key] = byCity[key] || []).push(ap);
    }
    const out = [];
    const seen = new Set();
    for (const ap of results) {
      const key = `${ap.city}|${ap.country_code || ap.country}`;
      if (seen.has(key)) continue;
      seen.add(key);
      const list = byCity[key];
      if (list.length > 1) {
        out.push({
          all: true, code: list.map((a) => a.code).join(','), city: ap.city, country: ap.country,
          country_code: ap.country_code, name: `All airports · ${list.map((a) => a.code).join(', ')}`,
        });
      }
      out.push(...list);
    }
    return out;
  }, [results]);

  return (
    <div className="relative" ref={ref}>
      <label className="block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">{label}</label>
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverAnchor asChild>
          <div className="flex items-center gap-2 rounded-xl border border-input bg-card px-3 h-12 focus-within:ring-2 focus-within:ring-accent">
            <Icon className="h-4 w-4 text-accent shrink-0" />
            <input
              data-testid={testid}
              className="w-full bg-transparent outline-none text-sm font-medium placeholder:text-muted-foreground/70"
              placeholder={placeholder}
              value={display}
              onFocus={beginEdit}
              onMouseDown={beginEdit}
              onChange={(e) => { setEditing(true); setDisplay(e.target.value); setQuery(e.target.value); setOpen(true); }}
            />
          </div>
        </PopoverAnchor>
        <PopoverContent align="start" className="p-0 w-[--radix-popover-trigger-width] min-w-[260px]"
          onOpenAutoFocus={(e) => e.preventDefault()}
          onInteractOutside={(e) => { if (ref.current && ref.current.contains(e.target)) e.preventDefault(); }}>
          <div className="max-h-72 overflow-auto py-1" data-testid="airport-autocomplete-results">
            {groups.length === 0 && <div className="px-3 py-4 text-sm text-muted-foreground">No airports found</div>}
            {groups.map((ap) => (
              <button key={ap.all ? `all-${ap.code}` : ap.code} onClick={() => pick(ap)}
                className={`w-full text-left px-3 py-2.5 hover:bg-secondary flex items-center gap-3 ${ap.all ? 'bg-accent/5' : ''}`}
                data-testid={ap.all ? `airport-option-all-${ap.city}` : `airport-option-${ap.code}`}>
                <span className={`grid place-items-center h-8 w-10 rounded-md text-xs font-mono font-semibold ${ap.all ? 'bg-accent/15 text-accent' : 'bg-secondary'}`}>{ap.all ? 'ALL' : ap.code}</span>
                <span className="min-w-0">
                  <span className="block text-sm font-medium truncate">{ap.city}, {ap.country}{ap.all ? ' — All airports' : ''}</span>
                  <span className="block text-xs text-muted-foreground truncate">{ap.name}</span>
                </span>
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};
